import { Box, Tab, Tabs, Typography, Chip, Link, Tooltip } from '@mui/material';
import { OfferwallMetrics } from '../../../types/offerwall.types';
import RevenueTable from './OfferwallRevenueTable';
import OfferwallPublisherTable from './OfferwallPublisherTable';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import * as React from 'react';

import type { Dayjs } from 'dayjs';

type Props = {
  offerwallMetrics: OfferwallMetrics | null;
  activeTab: number;
  setActiveTab: (value: number) => void;
  activeSubTab: number;
  setActiveSubTab: (value: number) => void;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

const networkNameMap = {
  flex: 'Flex',
  buzzvil: 'Buzzvil',
  ive: 'IVE',
  pincrux: 'Pincrux',
  nasmedia: 'NAS',
  igaworks: 'IGAWorks',
  gpa: 'GPA',
  ADForus: 'Adforus',
  Quantumbit2: 'Quantumbit',
  KEYWORDLAB: 'Keywordlab',
  nas: 'NAS',
  MindSoftLab: 'MindSoftLab',
  OHC: 'OHC'
} as const;

export default function OfferwallDashboard({
  offerwallMetrics,
  activeTab,
  setActiveTab,
  activeSubTab,
  setActiveSubTab,
  selectedApp,
  startDate,
  endDate,
}: Props) {
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!offerwallMetrics) return null;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const revenueDelta =
    (offerwallMetrics?.totalRevenue ?? 0) - (offerwallMetrics?.previousTotalRevenue ?? 0);
  const revenueDeltaPercentage = revenueDelta > 0 ? '▲' : '▼';

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 3, mt: 3, minWidth: 0 }}>
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            backgroundColor: '#e9edf5',
            borderRadius: 2,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minHeight: 100,
            minWidth: 0, // ✅ flex item 수축 허용
            overflow: 'hidden',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
              총 매출
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="#0044D2">
              {formatCurrency(offerwallMetrics?.totalRevenue ?? 0)}
            </Typography>
            <Typography
              variant="caption"
              fontSize="0.9rem"
              sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}
            >
              전월 동기간 대비{' '}
              <Box
                component="span"
                fontSize="0.8rem"
                sx={{
                  color: revenueDelta > 0 ? '#D32F2F' : '#1976D2',
                  display: 'inline-flex',
                  alignItems: 'center',
                  ml: 0.5,
                }}
              >
                {revenueDeltaPercentage}{' '}
                {Math.abs(
                  (revenueDelta / ((offerwallMetrics?.previousTotalRevenue ?? 0) || 1)) * 100
                ).toFixed(0)}
                %
              </Box>{' '}
              ({revenueDelta > 0 ? '+' : ''}
              {formatCurrency(revenueDelta)})
            </Typography>
            </Box>
            <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
              순수익
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="#0044D2">
              {formatCurrency((offerwallMetrics?.totalRevenue ?? 0) - (offerwallMetrics?.totalCost ?? 0))}
            </Typography>
            <Typography
              variant="caption"
              fontSize="0.9rem"
              sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}
            >
              비용 (-{formatCurrency(offerwallMetrics?.totalCost ?? 0)})
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            border: '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            borderRadius: 2,
            p: 3,
            flex: 1,
            minHeight: 100,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: 'center' }}>
            일별 매출 추이
          </Typography>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={offerwallMetrics.dailyPublisherRevenue}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0044D2" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0044D2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tickFormatter={(value: string) => {
                  const date = new Date(value);
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }}
              />
              <YAxis tickFormatter={(value: number) => `${(value / 1000).toFixed(0)}K`} />
              <RechartsTooltip
                formatter={(value: number) => formatCurrency(value)}
                labelFormatter={(value: string) =>
                  new Date(value).toLocaleDateString('ko-KR')
                }
              />
              <Area
                type="monotone"
                dataKey="dailyRevenue"
                stroke="#0044D2"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorRevenue)"
                name="매출"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
          📈 유저지급 TOP 오퍼월 광고
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, minWidth: 0 }}>
          {offerwallMetrics?.top5RevenueOfferwalls?.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                backgroundColor: '#ffffff',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                flex: 1,
                position: 'relative',
                textAlign: 'center',
                minWidth: 0, // ✅ 중요
                overflow: 'hidden',
              }}
            >
              <Box sx={{ width: '100%', minWidth: 0, mb: 1 }}>
                <Tooltip title={item.title}>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    noWrap
                    sx={{
                      width: '100%',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      minWidth: 0,
                      overflowWrap: 'anywhere', // 긴 연속 문자열 대응
                    }}
                  >
                    {item.title}
                  </Typography>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${item.reward}캐시`}
                  variant="outlined"
                  size="small"
                  sx={{ borderColor: '#0044D2', color: '#0044D2' }}
                />
                <Chip
                  label={`${networkNameMap[item.networkName as keyof typeof networkNameMap] ?? item.networkName}`}
                  variant="outlined"
                  size="small"
                />
              </Box>
              <Typography variant="body2" fontWeight="bold">
                {item.totalCash.toLocaleString()} 캐시
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
          📉 유저지급 LOW 오퍼월 광고
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, minWidth: 0 }}>
          {offerwallMetrics?.bottom5RevenueOfferwalls?.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                backgroundColor: '#ffffff',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                flex: 1,
                position: 'relative',
                textAlign: 'center',
                minWidth: 0, // ✅ 중요
                overflow: 'hidden',
              }}
            >
              <Box sx={{ width: '100%', minWidth: 0, mb: 1 }}>
                <Tooltip title={item.title}>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    noWrap
                    sx={{
                      width: '100%',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      minWidth: 0,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {item.title}
                  </Typography>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${item.reward}캐시`}
                  variant="outlined"
                  size="small"
                  sx={{ borderColor: '#0044D2', color: '#0044D2' }}
                />
                <Chip
                  label={`${networkNameMap[item.networkName as keyof typeof networkNameMap] ?? item.networkName}`}
                  variant="outlined"
                  size="small"
                />
              </Box>
              <Typography variant="body2" fontWeight="bold">
                {item.totalCash.toLocaleString()} 캐시
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mt: 2 }}>
        <Tab label="일별 매출" />
        <Tab label="매체사별 현황" />
      </Tabs>

      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          mt: 2,
          minWidth: 0,
          overflow: 'hidden',
          width: activeTab === 0 ? '100%' : '67%'
        }}
      >
        {activeTab === 0 && <RevenueTable data={offerwallMetrics} selectedApp={selectedApp} startDate={startDate} endDate={endDate} />}
        {activeTab === 1 && (
          <>
            <Box
              sx={{
                px: 2,
                pt: 2,
                pb: 1,
                display: 'flex',
                gap: 3,
                borderBottom: '1px solid #e0e0e0',
                flexWrap: 'wrap',
              }}
            >
              {[
                { label: '전체', value: 0 },
                { label: 'Buzzvil', value: 1 },
                { label: 'IVE', value: 2 },
                { label: 'Pincrux', value: 3 },
                { label: 'NAS', value: 4 },
                { label: 'Flex', value: 5 },
                { label: 'IGAWorks', value: 6 },
                { label: 'GPA', value: 7 },
                { label: 'Adforus', value: 8 },
                { label: 'Quantumbit', value: 9 },
                { label: 'Keywordlab', value: 10 },
                { label: 'Nasmedia', value: 11 },
                { label: 'MindSoftLab', value: 12 },
                { label: 'OHC', value: 13 },
              ].map((item) => (
                <Link
                  key={item.value}
                  component="button"
                  onClick={() => setActiveSubTab(item.value)}
                  sx={{
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    border: 'none',
                    backgroundColor: 'transparent',
                    padding: 0,
                    color: activeSubTab === item.value ? '#0044D2' : '#666',
                    fontWeight: activeSubTab === item.value ? 'bold' : 'normal',
                    borderBottom:
                      activeSubTab === item.value
                        ? '2px solid #0044D2'
                        : '2px solid transparent',
                    pb: 0.5,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'none', color: '#0044D2' },
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Box>
            <Box sx={{ width: '100%' }}>
              <OfferwallPublisherTable data={offerwallMetrics} activeSubTab={activeSubTab} selectedApp={selectedApp} startDate={startDate} endDate={endDate} />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
